/**
 * GRIDHEIMR — Metrics Chart Component
 *
 * Simple visual chart component for displaying metrics values.
 */

import React from "react";
import { BLUE, YELLOW, GREEN, RED, DARK_BG, PIXEL, MONO } from "../theme";

/**
 * Component props
 */
interface MetricsChartProps {
  /**
   * Chart title
   */
  title: string;
  /**
   * Current value to display
   */
  value: number;
  /**
   * Maximum value for percentage calculation (optional)
   */
  max?: number;
  /**
   * Unit to display (e.g., "%", "MB", "MB/s")
   */
  unit: string;
  /**
   * Icon to display
   */
  icon: string;
  /**
   * Color theme for the chart
   */
  color?: string;
}

/**
 * Get color based on percentage threshold
 */
function getThresholdColor(percent: number): string {
  if (percent >= 90) return RED;
  if (percent >= 75) return YELLOW;
  if (percent >= 50) return BLUE;
  return GREEN;
}

/**
 * Format number with commas for readability
 */
function formatNumber(num: number): string {
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

/**
 * Metrics Chart Component
 *
 * Displays a metric value with a visual progress bar.
 */
export function MetricsChart({
  title,
  value,
  max,
  unit,
  icon,
  color,
}: MetricsChartProps) {
  // Calculate percentage if max is provided
  const percentage = max && max > 0 ? (value / max) * 100 : 0;
  const displayPercentage = Math.min(Math.max(percentage, 0), 100);

  // Use custom color or threshold-based color
  const barColor = color || (max ? getThresholdColor(displayPercentage) : BLUE);

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <span style={styles.icon}>{icon}</span>
        <span style={styles.title}>{title}</span>
      </div>

      {/* Value Display */}
      <div style={styles.valueContainer}>
        <span style={styles.value}>{formatNumber(value)}</span>
        <span style={styles.unit}>{unit}</span>
        {max && max > 0 && (
          <span style={styles.maxValue}> / {formatNumber(max)}</span>
        )}
      </div>

      {/* Progress Bar (only if max is provided) */}
      {max && max > 0 && (
        <div style={styles.progressContainer}>
          <div
            style={{
              ...styles.progressBar,
              width: `${displayPercentage}%`,
              backgroundColor: barColor,
              boxShadow: `0 0 8px ${barColor}40`,
            }}
          />
        </div>
      )}

      {/* Percentage (only if max is provided) */}
      {max && max > 0 && (
        <div style={styles.percentageText}>
          {displayPercentage.toFixed(1)}% utilized
        </div>
      )}
    </div>
  );
}

/**
 * Inline styles
 */
const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
    padding: "1rem",
    background: DARK_BG,
    border: `1px solid rgba(0,106,167,0.1)`,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  icon: {
    fontSize: "1.25rem",
  },
  title: {
    fontFamily: PIXEL,
    fontSize: "0.42rem",
    fontWeight: 400,
    color: "rgba(255,255,255,0.5)",
    letterSpacing: "0.08em",
  },
  valueContainer: {
    display: "flex",
    alignItems: "baseline",
    gap: "0.25rem",
  },
  value: {
    fontFamily: MONO,
    fontSize: "2rem",
    fontWeight: 600,
    color: "rgba(255,255,255,0.87)",
    lineHeight: 1,
  },
  unit: {
    fontFamily: MONO,
    fontSize: "1rem",
    fontWeight: 500,
    color: "rgba(255,255,255,0.5)",
  },
  maxValue: {
    fontFamily: MONO,
    fontSize: "1rem",
    fontWeight: 400,
    color: "rgba(255,255,255,0.3)",
  },
  progressContainer: {
    width: "100%",
    height: "4px",
    background: "rgba(0,106,167,0.1)",
    overflow: "hidden",
  },
  progressBar: {
    height: "100%",
    transition: "width 0.3s ease, background-color 0.3s ease",
  },
  percentageText: {
    fontFamily: MONO,
    fontSize: "0.75rem",
    color: "rgba(255,255,255,0.4)",
    textAlign: "center",
  },
};
